import { useState, useEffect, useRef } from 'react';
import { Menu, MenuVariant, MenuSize } from '@/shared/components/Menu';
import { Tooltip } from '@/shared/components/Tooltip';
import EquipmentIcon from '@/assets/svg/equipment-outline.svg';
import UsersIcon from '@/assets/svg/card-users-icon.svg';
import PinIcon from '@/assets/svg/card-pin-icon.svg';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { FACILITY_EDIT_ERROR_MESSAGES } from '../../errors/errorMessages';
import type { FacilityListItem } from '../../types/facilityListType';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FacilityCardProps {
  facility: FacilityListItem;
  /**
   * SVG React component rendered as the decorative illustration on the left
   * side of the card (inside the circular background). Pass any SVG imported
   * via SVGR — e.g. `import FacilityIllustration from '@/assets/svg/facility-building-illustration.svg'`.
   */
  illustration: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onMenuAction: (action: string, facility: FacilityListItem) => void;
  canEdit?: boolean;
  index?: number;
}

// ─── Icon badge ───────────────────────────────────────────────────────────────

interface IconBadgeProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  truncate?: boolean;
}

const IconBadge = ({
  icon: Icon,
  label,
  truncate: shouldTruncate,
}: IconBadgeProps) => (
  <div
    className={`flex items-center gap-2 ${shouldTruncate ? 'min-w-0' : 'shrink-0'}`}
  >
    <div className="bg-[#223746] flex items-center justify-center rounded-full p-1 shrink-0">
      <Icon width={16} height={16} className="text-white" />
    </div>
    {shouldTruncate ? (
      <Tooltip
        content={label}
        side="bottom"
        align="left"
        useContentWidth
        contentMaxWidth={300}
      >
        <span className="text-xs text-[#474a4c] leading-4 tracking-[0.4px] block truncate min-w-0 w-full">
          {label}
        </span>
      </Tooltip>
    ) : (
      <span className="text-xs text-[#474a4c] leading-4 tracking-[0.4px] whitespace-nowrap">
        {label}
      </span>
    )}
  </div>
);

// ─── Dots vertical icon ───────────────────────────────────────────────────────

const DotsVerticalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── FacilityCard ─────────────────────────────────────────────────────────────

/**
 * Reusable facility card used in the CLIENT-role facility list page.
 *
 * Renders a 263px-height card matching the Figma "Upload File dialog" design:
 * - Decorative circle (bg-[#e8eef0]) overflowing from the left edge
 * - Illustration SVG passed via the `illustration` prop
 * - Facility name, ID, created date, status chip, equipment/workers/address rows
 * - 3-dot overflow menu (Podgląd / Edytuj)
 *
 * Pass a different `illustration` SVG to reuse this card for other entity types.
 */
export const FacilityCard = ({
  facility,
  illustration: Illustration,
  onMenuAction,
  canEdit = false,
  index,
}: FacilityCardProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const isActive = facility.status === 'active';

  const createdDate = new Date(facility.createdAt)
    .toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\./g, '/')
    .split('/')
    .reverse()
    .join('/');

  return (
    <div
      className="relative overflow-hidden bg-[#E8EEF0]/8 border border-[#e8eef0] rounded-xl h-[263px] shadow-[0px_2px_4px_rgba(0,0,0,0.08)]"
      role="article"
      aria-label={`Placówka: ${facility.name}`}
    >
      {/* Decorative circle — overflows left edge */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[#e8eef0]"
        style={{ left: '-160px' }}
        aria-hidden="true"
      />

      {/* Illustration — sits on top of circle */}
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{ left: '13px', width: '93px', height: '103px' }}
        aria-hidden="true"
      >
        <Illustration width={93} height={103} />
      </div>

      {/* 3-dot menu — top-right */}
      <div ref={menuAnchorRef} className="absolute top-2 right-2 z-10">
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
          data-testid={`facility-menu-button-${index ?? 0}`}
          aria-label={`Akcje dla placówki ${facility.name}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
        >
          <DotsVerticalIcon />
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 z-50">
            <Menu
              items={[
                { id: 'VIEW', label: 'Podgląd' },
                {
                  id: 'EDIT',
                  label: 'Edytuj',
                  disabled: !canEdit,
                  disabledTooltip:
                    FACILITY_EDIT_ERROR_MESSAGES.ACCESS_DENIED_TOOLTIP,
                  disabledTooltipSide: 'left',
                },
              ]}
              variant={MenuVariant.Light}
              size={MenuSize.Small}
              toolTip={false}
              onItemClick={(itemId) => {
                setMenuOpen(false);
                onMenuAction(itemId, facility);
              }}
              showLeadingIcons={false}
            />
          </div>
        )}
      </div>

      {/* Content — starts after the decorative circle */}
      <div
        className="absolute inset-y-0 right-0 flex flex-col justify-between py-[23px]"
        style={{ left: '163px', paddingRight: '40px' }}
      >
        {/* Name + ID + date */}
        <div className="flex flex-col gap-1">
          <Tooltip
            content={facility.name}
            side="bottom"
            align="right"
            useContentWidth
            contentMaxWidth={300}
          >
            <p className="text-[18px] font-normal text-[#181a1b] leading-[26px] truncate pr-8">
              {facility.name}
            </p>
          </Tooltip>
          <div className="flex flex-col gap-1 text-xs font-medium text-[#474a4c] tracking-[0.4px]">
            <p>
              <span className="font-normal">ID placówki:</span>
              {` ${facility.facilityId} `}
            </p>
            <p>
              <span className="font-normal">
                {FACILITY_LABELS.CREATED_DATE_LABEL}:
              </span>
              {` ${createdDate}`}
            </p>
          </div>
        </div>

        {/* Status chip — Material Design input chip style */}
        <div>
          <div
            className={`inline-flex items-center gap-2 pl-2 pr-3 py-1 rounded-lg ${
              isActive ? 'bg-[#e6f6f0]' : 'bg-[#f2f4f5]'
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                isActive ? 'bg-[#009c6a]' : 'bg-[#878b8e]'
              }`}
            />
            <span
              className={`text-sm font-medium leading-5 tracking-[0.1px] ${
                isActive ? 'text-[#009c6a]' : 'text-[#474a4c]'
              }`}
            >
              {isActive ? 'Aktywny' : 'Nieaktywny'}
            </span>
          </div>
        </div>

        {/* Info rows — equipment / workers / address */}
        <div className="flex flex-col gap-3">
          <IconBadge
            icon={EquipmentIcon}
            label={`${facility.equipmentCount} ${FACILITY_LABELS.SECTION_EQUIPMENT}`}
          />
          <IconBadge
            icon={UsersIcon}
            label={`${facility.facilityUserCount} ${FACILITY_LABELS.SECTION_FACILITY_USER}`}
          />
          <IconBadge
            icon={PinIcon}
            label={
              [facility.addressLine1, facility.city, facility.zipCode]
                .filter(Boolean)
                .join(', ') || '—'
            }
            truncate
          />
        </div>
      </div>
    </div>
  );
};
